// @flow

import * as React from 'react';

import debounce from 'lodash/debounce';
import { superpathjoin } from 'superpathjoin';

import { selectPricingInfos } from '@gandi/billing';
import { DateTime, useTranslator, mapTags } from '@gandi/translate';
import {
  Base,
  Button,
  Spinner,
  Svg,
  Text,
  ToggleGroup,
  TogglePill,
} from '@gandi/ui';
import { ListItem, ListRow, TitleCell } from '@gandi/ui/deprecated';

import { InputTextWithAddon } from 'gandi.react-form';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type {
  Quotation,
  QuotationPostPayload,
} from '../../modules/cloud/resources/quotations';
import { selectAdminURL } from '../../modules/config';

import { defaultMode, useQuotations } from '../modules/quotations';

import styles from './AddCredit.scss';

export type Props = {
  cancelUrl?: string,
  mode?: QuotationPostPayload[number]['modes'][number],
  onAddToCart: (quotation: Quotation) => Promise<any>,
  server?: QuotationPostPayload[number]['server'],
};

const splitRegexp = /([a-zA-Z]+)/;

function AddCredit({
  cancelUrl,
  mode = defaultMode,
  onAddToCart,
  server,
}: Props): React.Node {
  const { __, formatCurrency, formatPrice } = useTranslator();

  const [loading, setLoading] = React.useState(false);

  // $FlowFixMe[incompatible-call]
  const pricingInfos = useAppSelector(selectPricingInfos);

  const cloudBalanceOverviewURL = useAppSelector(state =>
    superpathjoin(
      selectAdminURL(state),
      'cloud',
      pricingInfos.sharingId,
      'balance',
      'overview',
    ),
  );
  // displayed amount in the input text
  const [amount, setAmount] = React.useState(mode.amount || 0);

  // current active pill
  const [pillValue, setPillValue] = React.useState(
    // $FlowFixMe[incompatible-type]
    'duration' in mode ? `${mode.duration}${mode.duration_unit}` : 'custom',
  );

  // payload sent to the `quotation` API
  const [selectedMode, setSelectedMode] = React.useState(mode);

  const [quotation, setQuotation] = React.useState<?Quotation>();
  const { handlePostQuotations, isPostingQuotations } = useQuotations();

  // format and set the amount to have only 2 digits after the dot
  function setFormattedAmount(value: number) {
    setAmount(parseFloat(parseFloat(value).toFixed(2)));
  }

  // update the displayed `amount` if needed: when current
  // selected `pill` change
  React.useEffect(() => {
    if (!isPostingQuotations) {
      handlePostQuotations([
        {
          // XXX: API BRAINFUCK
          consumption: {
            duration: 30,
            duration_unit: 'd',
          },
          modes: [selectedMode],
          server,
        },
      ]).then(response => {
        const q = response.data[0];
        setQuotation(q);
        setFormattedAmount(
          q.prices[0].normal_price_after_taxes !== undefined
            ? q.prices[0].normal_price_after_taxes
            : q.prices[0].price_after_taxes,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMode]);

  function handleToggleChange(evt: SyntheticInputEvent<>) {
    const value = evt.target.value;

    setPillValue(value);

    if (value !== 'custom') {
      // split by capturing string group to split `30d` to `["30", "d", ""]`
      const splitString = value.split(splitRegexp);
      setSelectedMode({
        duration: parseInt(splitString[0], 10),
        duration_unit: splitString[1],
      });
    }
  }

  function onInputChange(value: number) {
    setSelectedMode({ amount: value });
    setPillValue('custom');
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputChange = React.useCallback(
    debounce(onInputChange, 500),
    [],
  );

  function handleInputChange(evt: SyntheticInputEvent<>) {
    const value = evt.target.value;
    const formattedValue = parseFloat(value);

    setAmount(formattedValue);

    if (!Number.isNaN(formattedValue) && formattedValue > 0) {
      debouncedInputChange(formattedValue);
    } else {
      debouncedInputChange.cancel();
      setAmount(formattedValue);
    }
  }

  function handleAddToCart(evt: SyntheticMouseEvent<>) {
    evt.preventDefault();

    if (quotation) {
      setLoading(true);
      onAddToCart(quotation)
        .then(() => {
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }

  return (
    <>
      <Base marginBottom="base">
        <ListRow separatorBottom>
          <ListItem>
            <TitleCell>{__('Credit your Cloud Account')}</TitleCell>
          </ListItem>
        </ListRow>
        <Base backgroundColor="surface" padding="small">
          <ToggleGroup
            disabled={loading}
            onChange={handleToggleChange}
            value={pillValue}
          >
            <TogglePill value="30d">{__('For 30 days')}</TogglePill>
            <TogglePill value="6m">{__('For 6 months')}</TogglePill>
            <TogglePill value="custom">{__('Custom amount')}</TogglePill>
          </ToggleGroup>
          <Base className={styles.input} paddingTop="small">
            <InputTextWithAddon
              disabled={isPostingQuotations || loading}
              leftAddon={formatCurrency(pricingInfos.currency)}
              min="0"
              noValidate
              onChange={handleInputChange}
              step="0.01"
              type="number"
              // $FlowFixMe[incompatible-type]
              value={isPostingQuotations ? '' : amount ?? ''}
            />
            {isPostingQuotations ? (
              <Spinner className={styles.spinner} name="dots" />
            ) : (
              <>
                {quotation && (
                  <>
                    {(quotation.prices[0].discounts || []).map(
                      (discount, index) => (
                        <Base
                          flex
                          flexAlignItemsCenter
                          // eslint-disable-next-line react/no-array-index-key
                          key={`discount-${index}`}
                          tagName="p"
                        >
                          <Svg
                            className={styles.icon}
                            name="checkmark"
                            theme="green"
                          />
                          {discount.description}
                        </Base>
                      ),
                    )}
                    {(quotation.prices[0].discounts_suggest || []).map(
                      (suggestion, index) => (
                        <Base
                          flex
                          flexAlignItemsCenter
                          // eslint-disable-next-line react/no-array-index-key
                          key={`suggestion-${index}`}
                          tagName="p"
                        >
                          <Svg
                            className={styles.icon}
                            name="gift"
                            theme="green"
                          />
                          {suggestion}
                        </Base>
                      ),
                    )}
                    {quotation.prices[0].normal_price_after_taxes !==
                      undefined && (
                      <Base className={styles.discount} tagName="p">
                        {mapTags(
                          __(
                            'You will then pay [[price:]] instead of [[amount:]]',
                          ),
                          () => ({
                            price: (
                              <Text weight="semibold">
                                {formatPrice(
                                  quotation.prices[0].price_after_taxes,
                                  pricingInfos.currency,
                                )}
                              </Text>
                            ),
                            amount: formatPrice(
                              // $FlowFixMe[incompatible-call]
                              quotation.prices[0].normal_price_after_taxes,
                              pricingInfos.currency,
                            ),
                          }),
                        )}
                      </Base>
                    )}
                    <Base
                      flex
                      flexAlignItemsCenter
                      marginTop="base"
                      marginBottom="none"
                      tagName="p"
                    >
                      <Svg
                        className={styles.icon}
                        name="successWire"
                        theme="success"
                      />
                      <span>
                        {mapTags(
                          __('Your resources will be running until [[date:]]'),
                          () => ({
                            date: (
                              <Text weight="semibold">
                                <DateTime format="LLL UTC">
                                  {quotation.prices[0].date_exhaust}
                                </DateTime>
                              </Text>
                            ),
                          }),
                        )}
                      </span>
                    </Base>
                  </>
                )}
              </>
            )}
          </Base>
        </Base>
      </Base>
      <Button
        grouped
        href={cancelUrl ? undefined : cloudBalanceOverviewURL}
        kind="outline"
        to={cancelUrl}
      >
        {__('Cancel')}
      </Button>
      <Button
        disabled={
          !amount ||
          isPostingQuotations ||
          !quotation ||
          // $FlowFixMe[invalid-compare]
          Date.now() >= new Date(quotation.prices[0].date_exhaust)
        }
        grouped
        loading={loading}
        onClick={handleAddToCart}
        type="button"
      >
        {__('Continue')}
      </Button>
    </>
  );
}

export default AddCredit;
